import './defaultMapStyles.css'
import 'mapbox-gl/dist/mapbox-gl.css'

import { useWhen } from '@speedlo/hooks'
import mapboxgl, { Map, MapboxOptions } from 'mapbox-gl'
import React from 'react'

import { appConfig } from '../../config'
import { useMap } from './useMap'

mapboxgl.baseApiUrl = appConfig.mapsEndpointBase

type TProps = NoChildren &
  Omit<MapboxOptions, 'container' | 'style' | 'center' | 'zoom'> & {
    isMobile?: boolean
  }

export const MapRender = React.memo<TProps>(
  ({ children, isMobile = false, ...options }) => {
    const mapModel = useMap()
    const ref = React.useRef<HTMLDivElement | null>(null)
    const optionsRef = React.useRef(options)

    useWhen(
      () => mapModel.initialized,
      () => {
        const map = new Map({
          container: ref.current!,
          style: appConfig.mapsStyleUrl,
          center: mapModel.center,
          zoom: mapModel.zoom,
          scrollZoom: false, // required for centered zoom
          touchZoomRotate: false, // required for centered zoom
          ...optionsRef.current,
        })

        map.on('styledata', mapModel.setMapLoaded)
        map.on('moveend', mapModel.updatePosition)
        // the mapbox onZoom event triggers even on the decimal value change
        map.on('zoomend', mapModel.updateZoom)

        // https://github.com/mapbox/mapbox-gl-js/pull/3876
        map.scrollZoom.disable() // required for centered zoom
        map.touchZoomRotate.disable() // required for centered zoom
        // @ts-ignore (@types/mapbox-gl doesn’t know the variant of the method with params)
        map.scrollZoom.enable({ around: 'center' })
        // @ts-ignore (@types/mapbox-gl doesn’t know the variant of the method with params)
        map.touchZoomRotate.enable({ around: 'center' })

        mapModel.setMap(map)
      },
    )

    React.useEffect(() => {
      return () => {
        mapModel.map?.remove()
      }
    }, [mapModel])

    return (
      <div
        ref={ref}
        style={{
          borderRadius: '0 0 0.5rem 0.5rem',
          overflow: 'hidden',
          height: isMobile ? '100%' : 'calc(100% - 3.75rem)',
        }}
      />
    )
  },
)
